import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashGet from 'lodash/fp/get';

import {
  SR_API_ACTION_ROOMMATE_SIGNUP,
  SR_API_ACTION_GUARANTOR_SIGNUP,
  SR_API_ACTION_CHECK_LOGIN_EXIST,
  SR_API_ACTION_LOGIN,
} from 'api/actionTypes';

import withApiState from 'client/helpers/withApiState';
import postWithState from 'client/actions/postWithState';
import branch from 'client/core/branch';
import log from 'client/lib/log';
import { SR_API_ERROR_ACTION_PAYLOAD_VALIDATION_FAILED } from 'common/errorCodes';
import typeCheck from 'common/util/typeCheck';

export const SR_ROOMMATE_SIGNUP_FORM_MODE_SIGNUP = 'SR_ROOMMATE_SIGNUP_FORM_MODE_SIGNUP';
export const SR_ROOMMATE_SIGNUP_FORM_MODE_LOGIN = 'SR_ROOMMATE_SIGNUP_FORM_MODE_LOGIN';

const isPhoneValidationError = (error) => {
  return error && error.name === SR_API_ERROR_ACTION_PAYLOAD_VALIDATION_FAILED;
};
const isIncorrectPasswordError = (error) => lodashGet('details.fields.password', error);
const isUnexpectedError = (error) => (
  error
  && !isPhoneValidationError(error)
  && !isIncorrectPasswordError(error)
);

@withApiState({
  roommateSignupApiState: {
    action: SR_API_ACTION_ROOMMATE_SIGNUP,
  },
  guarantorSignupApiState: {
    action: SR_API_ACTION_GUARANTOR_SIGNUP,
  },
  checkLoginExistsApiState: {
    action: SR_API_ACTION_CHECK_LOGIN_EXIST,
  },
  loginApiState: {
    action: SR_API_ACTION_LOGIN,
  },
})
@branch()
class SrRoommateSignupForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    roommateSignupApiState: PropTypes.object.isRequired,
    guarantorSignupApiState: PropTypes.object.isRequired,
    checkLoginExistsApiState: PropTypes.object.isRequired,
    loginApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      formState: {
        name: '',
        phone: '',
        password: '',
      },
      userExistsByPhoneMap: {},
      isRoommate: false,
    };
  }

  submitForm = (event) => {
    event.preventDefault();
    const { formState, userExistsByPhoneMap, isRoommate } = this.state;
    const { onSubmit } = this.props;

    const userExistsByPhone = userExistsByPhoneMap[formState.phone].exists;

    if (userExistsByPhone && !isRoommate) {
      onSubmit({
        phone: formState.phone,
        password: formState.password,
        mode: SR_ROOMMATE_SIGNUP_FORM_MODE_LOGIN,
      });
    }
    else {
      onSubmit({
        name: formState.name,
        phone: formState.phone,
        password: formState.password,
        mode: SR_ROOMMATE_SIGNUP_FORM_MODE_SIGNUP,
      });
    }
  }

  componentWillUnmount() {
    this.isUnmounting = true;
  }

  checkIfPhoneExists(phone) {
    const { dispatch } = this.props;
    const { userExistsByPhoneMap } = this.state;

    typeCheck('phone::Phone', phone);
    if (!userExistsByPhoneMap[phone]) {
      this.setState({
        userExistsByPhoneMap: {
          ...userExistsByPhoneMap,
          [phone]: {
            isLoading: true,
            exists: null,
            phone: phone,
          },
        },
      });

      dispatch(postWithState, {
        action: SR_API_ACTION_CHECK_LOGIN_EXIST,
        payload: {
          phone,
        },
      }).then(this.onPhoneExistsCheck)
        .catch(log.error);
    }
  }

  onPhoneExistsCheck = ({ phone, exists, signupProvider }) => {
    const { userExistsByPhoneMap } = this.state;
    if (this.isUnmounting) {
      return;
    }

    typeCheck('exists::Boolean', exists);

    this.setState({
      userExistsByPhoneMap: {
        ...userExistsByPhoneMap,
        [phone]: {
          ...userExistsByPhoneMap[phone],
          isLoading: false,
          exists: exists,
        },
      },
      isRoommate: signupProvider === 'roommate',
    });
  };

  onInputChange = (event, field) => {
    const value = event.target.value;

    if (field !== 'phone') {
      this.setState({
        formState: {
          ...this.state.formState,
          [field]: value,
        },
      });
      return;
    }

    const valueWithoutNonDigits = value.replace(/\D/g, '');
    if (valueWithoutNonDigits.length > 10) {
      return;
    }

    this.setState({
      formState: {
        ...this.state.formState,
        [field]: valueWithoutNonDigits,
      },
    });

    // Check if a user already exists for this phone number
    if (valueWithoutNonDigits.length === 10) {
      this.checkIfPhoneExists(valueWithoutNonDigits);
    }
  }

  render() {
    const {
      roommateSignupApiState,
      guarantorSignupApiState,
      checkLoginExistsApiState,
      loginApiState,
    } = this.props;

    const {
      formState,
      userExistsByPhoneMap,
      isRoommate,
    } = this.state;

    const isBusy = (
      roommateSignupApiState.inProgress
      || checkLoginExistsApiState.inProgress
      || guarantorSignupApiState.inProgress
    );

    const userExistsByPhone = userExistsByPhoneMap[formState.phone]
      ? userExistsByPhoneMap[formState.phone].exists
      : false;

    const isPhoneFilled = formState.phone.length === 10;

    return (
      <form onSubmit={this.submitForm}>
        <input
          type='tel'
          pattern='[0-9]*'
          placeholder='Mobile Phone'
          value={formState.phone}
          onChange={(event) => this.onInputChange(event, 'phone')}
          name='roommatePhoneInput'
        />
        {isPhoneValidationError(roommateSignupApiState.error) && (
          <div className='formMessage error'>
            <span>Sorry, that phone number is not a valid 10-digit phone number</span>
          </div>
        )}
        {isPhoneFilled && (!userExistsByPhone || isRoommate) && !checkLoginExistsApiState.inProgress && (
          <input
            type='text'
            placeholder='Name'
            value={formState.name}
            onChange={(event) => this.onInputChange(event, 'name')}
            name='roommateNameInput'
          />
        )}
        {isPhoneFilled && (
          <input
            type='password'
            placeholder='Password'
            value={formState.password}
            onChange={(event) => this.onInputChange(event, 'password')}
            name='roommatePasswordInput'
          />
        )}
        {isIncorrectPasswordError(loginApiState.error) && (
          <div className='formMessage error'>
            <span>Whoops, your password is incorrect</span>
          </div>
        )}
        <button
          type='submit'
          className='button yellow'
          disabled={
            isBusy
            || !isPhoneFilled
            || !formState.password
            || (!formState.name && !userExistsByPhone)
          }
        >
          {isBusy ? 'loading...' : (userExistsByPhone ? 'log in' : 'continue')}
        </button>
        {isUnexpectedError(roommateSignupApiState.error) && (
          <div className='formMessage error'>
            {roommateSignupApiState.message}
          </div>
        )}
      </form>
    );
  }
}

SrRoommateSignupForm.displayName = 'SrRoommateSignupForm';

export default SrRoommateSignupForm;
